import { cn } from '@repo/tailwind-config/tailwind.functions'
import { LabelList, Pie, PieChart as PieChartComponent } from 'recharts'

import Typography from '../../atoms/typography'
import ChartContainer from './chart-container'
import { GraphData } from './utils'

interface PieChartData extends GraphData {
  /**
   * fill color of the pie chart
   */
  fill: string

  /**
   * legend label of the pie chart
   */
  legendLabel?: string
}

interface PieChartProps {
  /**
   * data to be displayed in the pie chart
   */
  data: PieChartData[]

  /**
   * whether to show the legend or not
   */
  legend?: boolean

  /**
   * whether to show the inner labels or not
   */
  showInnerLabels?: boolean

  /**
   * size of the pie chart container
   */
  size?: 'small' | 'medium' | 'large' | 'xl'

  /**
   * className for the label list
   */
  labelListClassName?: string
}

interface LegendProps {
  color: string
  label: string
}

const Legend = ({ label, color }: LegendProps) => {
  return (
    <div className={'flex gap-[10px] flex-row items-center'}>
      <div className={'w-4 h-4 rounded-xs'} style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  )
}

const PieChart = ({
  data,
  legend = true,
  labelListClassName,
  showInnerLabels = true,
  size = 'medium'
}: PieChartProps) => {
  const filteredData = data.filter((item) => item.value > 0)

  if (filteredData.length === 0)
    return (
      <div className={'w-full text-center justify-center flex items-center h-full'}>
        <Typography variant="secondary-body">Aucune donnée à afficher</Typography>
      </div>
    )

  const total = filteredData.reduce((acc, item) => acc + item.value, 0)

  // Size configurations
  const sizeConfig = {
    small: { containerClass: 'w-64 h-64', pieRadius: 80 },
    medium: { containerClass: 'w-80 h-80', pieRadius: 120 },
    large: { containerClass: 'w-96 h-96', pieRadius: 150 },
    xl: { containerClass: 'w-[500px] h-[500px]', pieRadius: 200 }
  }

  const currentSize = sizeConfig[size]

  return (
    <div className={'flex flex-col'}>
      <ChartContainer config={{}} className={`mx-auto ${currentSize.containerClass}`}>
        <PieChartComponent>
          <Pie
            data={filteredData}
            dataKey={'value'}
            cx="50%"
            cy="50%"
            outerRadius={currentSize.pieRadius}
            innerRadius={0}
          >
            {showInnerLabels && (
              <LabelList
                dataKey={'key'}
                className={cn('fill-background', labelListClassName)}
                stroke={'none'}
                fontSize={14}
                formatter={(value: string) => {
                  const targetValue = data.find((item) => item.key === value)?.value

                  return targetValue
                    ? `${targetValue} (${((targetValue / total) * 100).toFixed(0)}%)`
                    : ''
                }}
              />
            )}
          </Pie>
        </PieChartComponent>
      </ChartContainer>

      {legend && (
        <div className={'w-full flex flex-wrap justify-center items-center gap-10 mt-4'}>
          {data.map((item, index) => (
            <Legend key={index} color={item.fill} label={item.legendLabel || item.key} />
          ))}
        </div>
      )}
    </div>
  )
}

export default PieChart
